import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@ant-design/v5-patch-for-react-19'
import './index.css'
import App from './App.jsx'
import { VERSION_INFO } from './utils/versionInfo.js'
import { requestPersistentStorage } from './utils/documentDraftStore.js'
import { startOutbox } from './utils/documentOutbox.js'

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,

)

requestPersistentStorage()
startOutbox()

// Detect iOS for platform-specific SW update handling.
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// iOS WebKit can get stuck zoomed in after a text field blurs (standalone/PWA
// mode doesn't reliably auto-reset viewport scale like Safari chrome does).
// Toggling the viewport meta forces WebKit to recompute and snap back to 1x.
if (isIOS) {
  const resetIOSZoom = () => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    const original = viewport.getAttribute('content');
    viewport.setAttribute('content', `${original}, maximum-scale=1`);
    setTimeout(() => viewport.setAttribute('content', original), 300);
  };
  document.addEventListener('focusout', resetIOSZoom);
}

// Register SW only in production builds
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((reg) => {
        console.log('Service Worker registered', reg);

        // UI-driven update flow via DOM events
        // We'll dispatch 'sw-update-available' and listen for 'sw-accept-update'
        let reloadRequested = false;
        let lastWaitingSW = null;
        const notifyUpdateAvailable = () => {
          window.dispatchEvent(new CustomEvent('sw-update-available'));
        };

        // Accept update from UI
        const onAcceptUpdate = () => {
          const waiting = reg.waiting || lastWaitingSW;
          if (waiting) {
            reloadRequested = true;
            waiting.postMessage('SKIP_WAITING');
            // iOS fallback: if controllerchange doesn't fire within 3s, force reload
            if (isIOS) {
              setTimeout(() => {
                if (reloadRequested && !refreshing) {
                  console.log('[SW] iOS: Force reloading after SKIP_WAITING timeout');
                  window.location.reload();
                }
              }, 3000);
            }
          } else {
            // Fallback: try to update registration and reload
            reloadRequested = true;
            reg.update().finally(() => {
              // If no controllerchange happens, force a reload
              setTimeout(() => {
                if (reloadRequested) window.location.reload();
              }, 1500);
            });
          }
        };
        window.addEventListener('sw-accept-update', onAcceptUpdate);

        // Listen for new installing worker and flag when installed
        reg.addEventListener('updatefound', () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              // New update available -> notify UI
              lastWaitingSW = reg.waiting || newSW;
              notifyUpdateAvailable();
            }
          });
        });

        // If there's already a waiting worker when we load (e.g., page was in background), notify UI
        if (reg.waiting) {
          lastWaitingSW = reg.waiting;
          notifyUpdateAvailable();
        }

        // Reload the page once when the new SW takes control (after user accepts)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!reloadRequested || refreshing) return;
          refreshing = true;
          console.log('[SW] Controller changed after update; reloading to new version');
          window.location.reload();
        });

        // Version watchdog: compare public/version.json to bundled VERSION_INFO
        const checkVersion = async () => {
          try {
            const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
            if (!res.ok) return;
            const server = await res.json();
            const serverHash = server?.commitHash || '';
            const localHash = VERSION_INFO?.commitHash || '';
            if (serverHash && localHash && serverHash !== localHash) {
              // Ask SW to update and notify UI
              reg.update().catch(() => {});
              notifyUpdateAvailable();
            }
          } catch (e) {
            // ignore network errors
          }
        };

        const proactiveCheck = () => {
          // Request SW to check for an update
          if (!reg.waiting && !lastWaitingSW) {
            reg.update().catch(() => {});
          }
          // And verify version.json
          checkVersion();
        };
        window.addEventListener('focus', proactiveCheck);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') proactiveCheck();
        });
        
        // iOS-specific: pageshow event fires when page comes from bfcache or background
        // This is more reliable than focus/visibility on iOS
        if (isIOS) {
          window.addEventListener('pageshow', (event) => {
            // persisted = true means page was restored from bfcache
            if (event.persisted || document.visibilityState === 'visible') {
              console.log('[SW] iOS: pageshow event, checking for updates');
              proactiveCheck();
            }
          });
        }
        
        // Every 15 minutes (5 minutes on iOS for more aggressive checking)
        setInterval(proactiveCheck, isIOS ? 5 * 60 * 1000 : 15 * 60 * 1000);
      })
      .catch((err) => console.error('Service Worker registration failed', err));
  });
}